import React, { useState } from "react";
import './BalanceSheet.css';

interface Props {
  cashFlowStatement: CashFlowStatementProps;
}

interface LineItem {
  name: string;
  value: number;
  account_id?: string;
  line_items?: LineItem[];
}

export interface CashFlowStatementProps {
  id: number;
  rootfi_id: number;
  period_start: string;
  period_end: string;
  operating_activities: LineItem[];
  investing_activities: LineItem[];
  financing_activities: LineItem[];
  cash_at_beginning_of_period: number;
  cash_at_end_of_period: number;
}

const CashFlowStatement: React.FC<Props> = ({ cashFlowStatement }) => {
  const [activeSection, setActiveSection] = useState<string>('Operating Activities');
  const [collapsedItems, setCollapsedItems] = useState<{ [key: string]: boolean }>({});

  // Handle toggling of line item collapse/expand
  const toggleCollapse = (itemName: string) => {
    setCollapsedItems(prevState => ({
      ...prevState,
      [itemName]: !prevState[itemName],
    }));
  };

  // Render line items recursively with collapsible functionality
  const renderLineItems = (lineItems?: LineItem[], level: number = 0) => {
    if (!lineItems || lineItems.length === 0) return null;

    return (
      <ul className="line-items">
        {lineItems.map((item, index) => (
          <li key={`${item.name}-${index}`} className="line-item" style={{ marginLeft: `${level * 20}px` }}>
            <div className="line-item-header" onClick={() => toggleCollapse(item.name)}>
              <div className="line-item-name">
                <strong>{item.name}</strong>
              </div>
              
              <div className="line-item-value">
                ${item.value.toLocaleString()}
              </div>

              {item.line_items && (
                <span className="collapse-toggle">
                  {collapsedItems[item.name] ? '+' : '-'}
                </span>
              )}
            </div>
            {item.account_id && <span className="account-id">(Account ID: {item.account_id})</span>}
            {item.line_items && !collapsedItems[item.name] && renderLineItems(item.line_items, level + 1)}
          </li>
        ))}
      </ul>
    );
  };

  // Render sections for operating, investing, and financing activities
  const renderSection = (title: string, data: LineItem[]) => (
    <section className={`balance-sheet-section ${activeSection === title ? 'active' : ''}`}>
      <h3 className="section-title">{title}</h3>
      <div>{renderLineItems(data)}</div>
    </section>
  );

  // Handle switching between sections
  const handleTabChange = (section: string) => {
    setActiveSection(section);
  };

  return (
    <div className="balance-sheet-container">
      <div className="balance-sheet-header">
        <h1 className="header-title">Cash Flow Statement</h1>
        <h2 className="header-subtitle">
          Period: {cashFlowStatement.period_start} to {cashFlowStatement.period_end}
        </h2>
      </div>

      {/* Quick Overview Section */}
      <div className="summary-section">
        <h3 className="summary-title">Quick Overview</h3>
        <div className="summary-cards">
          <div className="summary-card">
            <h4>Cash at Beginning of Period</h4>
            <p>${cashFlowStatement.cash_at_beginning_of_period.toLocaleString()}</p>
          </div>
          <div className="summary-card">
            <h4>Cash at End of Period</h4>
            <p>${cashFlowStatement.cash_at_end_of_period.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Tabs for switching between sections */}
      <div className="tabs">
        <button
          className={`tab ${activeSection === 'Operating Activities' ? 'active' : ''}`}
          onClick={() => handleTabChange('Operating Activities')}
        >
          Operating Activities
        </button>
        <button
          className={`tab ${activeSection === 'Investing Activities' ? 'active' : ''}`}
          onClick={() => handleTabChange('Investing Activities')}
        >
          Investing Activities
        </button>
        <button
          className={`tab ${activeSection === 'Financing Activities' ? 'active' : ''}`}
          onClick={() => handleTabChange('Financing Activities')}
        >
          Financing Activities
        </button>
      </div>

      {/* Render the active section */}
      <div className="balance-sheet-content">
        {activeSection === 'Operating Activities' && renderSection('Operating Activities', cashFlowStatement.operating_activities)}
        {activeSection === 'Investing Activities' && renderSection('Investing Activities', cashFlowStatement.investing_activities)}
        {activeSection === 'Financing Activities' && renderSection('Financing Activities', cashFlowStatement.financing_activities)}
      </div>
    </div>
  );
};

export default CashFlowStatement;
